import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { Card, Etiqueta, Meta, Pilula, cores } from "../../relatorio/styles";
import { desdeQuando } from "../pages/estados";
import { linkParaReivindicar, situacaoDoPedido } from "../pages/acesso";
import { iniciais } from "../pages/contas";
import { LinkDeAcesso } from "./link-de-acesso";
import type { CategoriaNoGoogle, ClienteNovo as DadosDoClienteNovo, ContaConectada, PedidoDeAcesso, PerfilNoGoogle, Reivindicacao, SituacaoNoGoogle } from "../types";
import { Verificacao } from "./verificacao";

const Painel = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  border: 1px solid ${cores.line};
`;

const Etapas = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
  @media (min-width: 1000px) {
    grid-template-columns: 1.2fr 1fr;
  }
`;

const Etapa = styled.div<{ $fundo: string }>`
  background: ${({ $fundo }) => $fundo};
  border-radius: 18px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  .cabeca {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 14px;
  }
  .numero {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: ${fonts.gilroyBold}, sans-serif;
    font-size: 13px;
  }
  input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${cores.line};
    border-radius: 1000px;
    padding: 11px 16px;
    font: inherit;
    font-size: 14px;
    outline: none;
    background: #fff;
  }
`;

const Resultado = styled.button<{ $ativo?: boolean }>`
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.blue : cores.line)};
  background: ${({ $ativo }) => ($ativo ? cores.blueTint : "#fff")};
  border-radius: 14px;
  padding: 10px 14px;
  font: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  &:hover {
    border-color: ${cores.blue};
  }
  .nome {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 14px;
  }
`;

const Conta = styled.button<{ $ativo?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid ${({ $ativo }) => ($ativo ? cores.blue : cores.line)};
  background: ${({ $ativo }) => ($ativo ? cores.blueTint : "#fff")};
  border-radius: 14px;
  padding: 8px 12px;
  font: inherit;
  cursor: pointer;
  min-width: 0;
  &:hover {
    border-color: ${cores.blue};
  }
  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: ${cores.greenTint};
    color: ${cores.green};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 11px;
    flex-shrink: 0;
  }
  .email {
    font-family: ${fonts.gilroySemiBold}, sans-serif;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
`;

const Alternar = styled.button`
  align-self: flex-start;
  border: 0;
  background: none;
  color: ${cores.blue};
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
`;

const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
`;

const ItemDePedido = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${cores.line};
  font-size: 14px;
  &:first-child {
    border-top: 0;
  }
`;

type Props = {
  contas: ContaConectada[];
  onBuscarPlaces: (texto: string) => Promise<{ lista: PerfilNoGoogle[]; erro: string | null }>;
  onGerarLink: (cliente: DadosDoClienteNovo) => Promise<PedidoDeAcesso | null>;
  onListarPedidos: () => Promise<PedidoDeAcesso[]>;
  onSituacaoNoGoogle: (dados: { placeId: string | null; name: string; address: string; managerUserId: number }) => Promise<SituacaoNoGoogle | null>;
  verificacao: {
    reivindicar: (cliente: DadosDoClienteNovo) => Promise<Reivindicacao | null>;
    opcoes: (tokenDoPedido: string) => Promise<Reivindicacao | null>;
    iniciar: (tokenDoPedido: string, dados: { method: string; phoneNumber?: string | null; emailAddress?: string | null }) => Promise<PedidoDeAcesso | null>;
    concluir: (tokenDoPedido: string, pin: string) => Promise<PedidoDeAcesso | null>;
    buscarCategorias: (texto: string) => Promise<CategoriaNoGoogle[]>;
  };
};



/**
 * Cliente novo: a equipe acha o perfil no Google Places, escolhe qual conta
 * gestora recebe o acesso e gera o mesmo link que o cliente abre para liberar.
 */
export const ClienteNovo = ({ contas, onBuscarPlaces, onGerarLink, onListarPedidos, onSituacaoNoGoogle, verificacao }: Props) => {
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState<PerfilNoGoogle[]>([]);
  const [erroDaBusca, setErroDaBusca] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [perfil, setPerfil] = useState<PerfilNoGoogle | null>(null);
  const [manual, setManual] = useState(false);
  const [nomeManual, setNomeManual] = useState("");
  const [enderecoManual, setEnderecoManual] = useState("");
  const [filtroDeConta, setFiltroDeConta] = useState("");
  const [conta, setConta] = useState<ContaConectada | null>(null);
  const [gerando, setGerando] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoDeAcesso[]>([]);
  const [ultimo, setUltimo] = useState<PedidoDeAcesso | null>(null);
  const [situacao, setSituacao] = useState<SituacaoNoGoogle | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [reivindicacao, setReivindicacao] = useState<Reivindicacao | null>(null);
  const [reivindicando, setReivindicando] = useState(false);
  const temporizador = useRef<number | null>(null);

  useEffect(() => {
    onListarPedidos().then(setPedidos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (temporizador.current) window.clearTimeout(temporizador.current);
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados([]);
      return undefined;
    }
    temporizador.current = window.setTimeout(async () => {
      setBuscando(true);
      const resposta = await onBuscarPlaces(termo);
      setResultados(resposta.lista);
      setErroDaBusca(resposta.erro);
      setBuscando(false);
    }, 450);
    return () => {
      if (temporizador.current) window.clearTimeout(temporizador.current);
    };
  }, [busca, onBuscarPlaces]);

  // Só contas que já conectaram ao Google podem aceitar o convite sozinhas.
  const contasElegiveis = useMemo(() => {
    const termo = filtroDeConta.trim().toLowerCase();
    return contas
      .filter((c) => c.hasToken && c.email)
      .filter((c) => !termo || c.email.toLowerCase().includes(termo) || (c.name ?? "").toLowerCase().includes(termo))
      .sort((a, b) => Number(b.connected) - Number(a.connected) || a.email.localeCompare(b.email));
  }, [contas, filtroDeConta]);

  const perfilPronto = manual ? nomeManual.trim().length >= 3 : perfil !== null;
  const nomeEscolhido = manual ? nomeManual.trim() : (perfil?.displayName ?? "");
  const placeEscolhido = manual ? null : (perfil?.profileId ?? null);
  const enderecoEscolhido = manual ? enderecoManual.trim() : (perfil?.formattedAddress ?? "");

  // Assim que perfil e conta estao escolhidos, pergunta ao Google se a ficha tem dono:
  // sem dono, o link para o cliente nao resolve e a tela precisa dizer isso antes.
  useEffect(() => {
    if (!perfilPronto || !conta) {
      setSituacao(null);
      return undefined;
    }
    let ativo = true;
    setConsultando(true);
    onSituacaoNoGoogle({ placeId: placeEscolhido, name: nomeEscolhido, address: enderecoEscolhido, managerUserId: conta.userId }).then((resposta) => {
      if (!ativo) return;
      setSituacao(resposta);
      setConsultando(false);
    });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilPronto, conta?.userId, placeEscolhido, nomeEscolhido, enderecoEscolhido]);

  const gerar = async () => {
    if (!perfilPronto || !conta) return;
    setGerando(true);
    const dados: DadosDoClienteNovo = manual || !perfil
      ? { placeId: null, name: nomeManual.trim(), address: enderecoManual.trim(), managerUserId: conta.userId }
      : { placeId: perfil.profileId, name: perfil.displayName, address: perfil.formattedAddress, managerUserId: conta.userId };
    const pedido = await onGerarLink(dados);
    setGerando(false);
    if (pedido) {
      setUltimo(pedido);
      setPedidos((atual) => [pedido, ...atual.filter((p) => p.token !== pedido.token)]);
    }
  };

  const reivindicar = async (categoryId?: string) => {
    if (!perfilPronto || !conta) return;
    setReivindicando(true);
    const resposta = await verificacao.reivindicar({ placeId: placeEscolhido, name: nomeEscolhido, address: enderecoEscolhido, managerUserId: conta.userId, categoryId: categoryId ?? null });
    setReivindicando(false);
    if (resposta) {
      setReivindicacao(resposta);
      setPedidos((atual) => [resposta.pedido, ...atual.filter((p) => p.token !== resposta.pedido.token)]);
    }
  };

  const atualizarPedido = (pedido: PedidoDeAcesso) => {
    setReivindicacao((atual) => (atual ? { ...atual, pedido } : atual));
    setPedidos((atual) => [pedido, ...atual.filter((p) => p.token !== pedido.token)]);
  };

  const retomarVerificacao = async (pedido: PedidoDeAcesso) => {
    setReivindicacao({ pedido, opcoes: [], erro: null });
    const resposta = await verificacao.opcoes(pedido.token);
    if (resposta) setReivindicacao(resposta);
  };

  const recomecar = () => {
    setUltimo(null);
    setReivindicacao(null);
    setPerfil(null);
    setConta(null);
    setBusca("");
    setResultados([]);
    setManual(false);
    setNomeManual("");
    setEnderecoManual("");
    setSituacao(null);
  };

  return (
    <Painel aria-label="Pegar acesso de cliente novo">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16 }}>Pegar acesso de cliente novo</span>
          <Meta style={{ display: "block", marginTop: 2 }}>Ache o perfil no Google, escolha o e-mail gestor que vai receber o acesso e mande o link para o cliente.</Meta>
        </div>
        {(ultimo || reivindicacao) && (
          <Pilula as="button" type="button" $tipo="contorno" onClick={recomecar} style={{ padding: "8px 16px", fontSize: 13 }}>
            Outro cliente
          </Pilula>
        )}
      </div>

      {reivindicacao ? (
        <Verificacao
          reivindicacao={reivindicacao}
          onIniciar={(dados) => verificacao.iniciar(reivindicacao.pedido.token, dados).then((p) => { if (p) atualizarPedido(p); return p; })}
          onConcluir={(pin) => verificacao.concluir(reivindicacao.pedido.token, pin).then((p) => { if (p) atualizarPedido(p); return p; })}
          onRecarregarOpcoes={() => verificacao.opcoes(reivindicacao.pedido.token).then((r) => { if (r) setReivindicacao(r); })}
          onTentarDeNovo={perfilPronto && conta ? (categoryId) => reivindicar(categoryId) : undefined}
          onBuscarCategorias={verificacao.buscarCategorias}
        />
      ) : ultimo ? (
        <Card $fundo={cores.greenTint} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15 }}>{`Link pronto para “${ultimo.profileName}”`}</span>
          <Meta>{`Acesso vai para ${ultimo.managerEmail} · vale 7 dias`}</Meta>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <code style={{ fontSize: 12, color: cores.ink3, overflowWrap: "anywhere" }}>{ultimo.link}</code>
            <LinkDeAcesso pedido={ultimo} gerando={false} onGerar={gerar} />
          </div>
        </Card>
      ) : (
        <>
          <Etapas>
            <Etapa $fundo={cores.blueTint}>
              <span className="cabeca">
                <span className="numero" style={{ background: "#1A73E8" }}>1</span>
                Qual é o perfil no Google?
              </span>
              <input
                type="search"
                placeholder="Nome da empresa e cidade (ex.: Clínica Sorriso Itaúna)"
                aria-label="Buscar perfil no Google"
                value={busca}
                onChange={(evento) => {
                  setBusca(evento.target.value);
                  setPerfil(null);
                }}
              />
              {buscando && <Meta>Buscando no Google…</Meta>}
              {!buscando && erroDaBusca && <Meta style={{ color: cores.red }}>{`A busca no Google falhou: ${erroDaBusca}`}</Meta>}
              {!buscando && !erroDaBusca && busca.trim().length >= 3 && resultados.length === 0 && <Meta>Nenhum perfil encontrado. Tente nome e cidade.</Meta>}
              {resultados.length > 0 && (
                <Lista>
                  {resultados.map((item) => (
                    <Resultado key={item.profileId} type="button" $ativo={perfil?.profileId === item.profileId} onClick={() => setPerfil(item)}>
                      <span className="nome">{item.displayName}</span>
                      <Meta>{item.formattedAddress}</Meta>
                    </Resultado>
                  ))}
                </Lista>
              )}
              {perfil && !manual && (
                <div>
                  <Etiqueta $cor="azul">{`Escolhido: ${perfil.displayName}`}</Etiqueta>
                </div>
              )}
              <Alternar type="button" onClick={() => setManual((atual) => !atual)}>
                {manual ? "Voltar para a busca no Google" : "Não achou? Digitar o nome do perfil"}
              </Alternar>
              {manual && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input type="text" placeholder="Nome do perfil como está no Google" aria-label="Nome do perfil" value={nomeManual} onChange={(evento) => setNomeManual(evento.target.value)} />
                  <input type="text" placeholder="Cidade/UF (opcional)" aria-label="Cidade do perfil" value={enderecoManual} onChange={(evento) => setEnderecoManual(evento.target.value)} />
                  <Meta>Sem o perfil do Google, o cliente escolhe a ficha certa na lista dele ao entrar com o Google.</Meta>
                </div>
              )}
            </Etapa>

            <Etapa $fundo={cores.greenTint}>
              <span className="cabeca">
                <span className="numero" style={{ background: "#188038" }}>2</span>
                Para qual e-mail gestor vai o acesso?
              </span>
              <input
                type="search"
                placeholder="Filtrar contas gestoras"
                aria-label="Filtrar contas gestoras"
                value={filtroDeConta}
                onChange={(evento) => setFiltroDeConta(evento.target.value)}
              />
              <Lista>
                {contasElegiveis.map((item) => (
                  <Conta key={item.userId} type="button" $ativo={conta?.userId === item.userId} onClick={() => setConta(item)}>
                    <span className="avatar">{iniciais(item.name, item.email)}</span>
                    <span className="email">{item.email}</span>
                    <Etiqueta $cor={item.connected ? "verde" : "vermelha"}>{item.connected ? "Conectada" : "Reconectar"}</Etiqueta>
                  </Conta>
                ))}
                {contasElegiveis.length === 0 && <Meta>Nenhuma conta gestora conectada com esse filtro.</Meta>}
              </Lista>
            </Etapa>
          </Etapas>

          {perfilPronto && conta && (consultando || situacao) && (
            <Card
              $fundo={consultando ? cores.grayTint : situacao?.temDono === false ? cores.redTint : situacao?.encontrada ? cores.greenTint : cores.yellowTint}
              $compacto
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {consultando && <Meta>Perguntando ao Google se a ficha tem dono…</Meta>}
              {!consultando && situacao?.temDono === false && (
                <>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, color: cores.red }}>Perfil sem dono no Google</span>
                  <Meta>
                    {`Ninguém reivindicou “${situacao.title ?? nomeEscolhido}” ainda. O link para o cliente não resolve aqui: ele entraria com o Google e a ficha não apareceria na lista dele. O caminho é reivindicar com a conta gestora ${situacao.managerEmail ?? conta.email} e concluir a verificação do Google (código por telefone, SMS ou e-mail do negócio).`}
                  </Meta>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <Pilula as="button" type="button" onClick={() => reivindicar()} disabled={reivindicando} style={{ padding: "9px 18px", fontSize: 13 }}>
                      {reivindicando ? "Reivindicando…" : `Reivindicar para ${conta.email}`}
                    </Pilula>
                    <Meta>cria a ficha na conta gestora agora; depois é só o código de verificação do Google</Meta>
                    <a href={linkParaReivindicar(placeEscolhido, nomeEscolhido)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                      ou fazer pelo Maps
                    </a>
                  </div>
                </>
              )}
              {!consultando && situacao?.temDono === true && (
                <>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, color: cores.green }}>Perfil tem dono no Google</span>
                  <Meta>
                    {`Alguém já administra “${situacao.title ?? nomeEscolhido}”. O Google não revela quem. Mande o link para o cliente (se ele for o dono, libera em 1 minuto) ou peça acesso pelo caminho oficial do Google com a conta gestora; o dono tem 3 dias para responder.`}
                  </Meta>
                  {situacao.requestAdminRightsUri && (
                    <div>
                      <Pilula $tipo="contorno" href={situacao.requestAdminRightsUri} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 16px", fontSize: 13 }}>
                        Pedir acesso no Google
                      </Pilula>
                      <Meta style={{ marginLeft: 10 }}>a página do Google mostra parte do e-mail do dono (ex.: “da…@gmail.com”) e o dono tem 3 dias para responder</Meta>
                    </div>
                  )}
                </>
              )}
              {!consultando && situacao && !situacao.encontrada && (
                <>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, color: cores.amber }}>Não achei essa ficha no Google</span>
                  <Meta>{situacao.erro ?? "O Google não devolveu nenhuma ficha com esse nome e endereço. Confira o nome ou tente pela busca."}</Meta>
                </>
              )}
            </Card>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <Pilula as="button" type="button" onClick={gerar} disabled={!perfilPronto || !conta || gerando} style={{ padding: "11px 22px", fontSize: 14, opacity: !perfilPronto || !conta ? 0.55 : 1 }}>
              {gerando ? "Gerando…" : situacao?.temDono === false ? "Gerar link mesmo assim" : "Gerar link para o cliente"}
            </Pilula>
            <Meta>
              {perfilPronto && conta
                ? `“${nomeEscolhido}” → ${conta.email}`
                : "Escolha o perfil e a conta gestora para liberar o botão."}
            </Meta>
          </div>
        </>
      )}

      {pedidos.length > 0 && (
        <div>
          <Meta style={{ display: "block", marginBottom: 4, fontFamily: fonts.gilroySemiBold, color: cores.ink3 }}>Links de clientes novos</Meta>
          <div>
            {pedidos.slice(0, 12).map((pedido) => {
              const situacao = situacaoDoPedido(pedido);
              return (
                <ItemDePedido key={pedido.token}>
                  <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontFamily: fonts.gilroySemiBold }}>{pedido.profileName}</span>
                    <Meta>{`${pedido.managerEmail} · gerado ${desdeQuando(pedido.createdAt)}${pedido.customerEmail ? ` · cliente ${pedido.customerEmail}` : ""}`}</Meta>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {pedido.googleClaimed === false && <Etiqueta $cor="vermelha">sem dono no Google</Etiqueta>}
                    {pedido.googleClaimed === true && <Etiqueta $cor="verde">tem dono</Etiqueta>}
                    <Etiqueta $cor={situacao.cor}>{situacao.rotulo}</Etiqueta>
                    {(pedido.status === "REIVINDICADA" || pedido.status === "VERIFICACAO_ENVIADA") && (
                      <button
                        type="button"
                        onClick={() => retomarVerificacao(pedido)}
                        style={{ border: `1px solid ${cores.line}`, background: "#fff", color: cores.blue, borderRadius: 1000, padding: "6px 12px", font: "inherit", fontSize: 12, cursor: "pointer" }}
                      >
                        Continuar verificação
                      </button>
                    )}
                    {pedido.status !== "CONCLUIDO" && pedido.status !== "REIVINDICADA" && pedido.status !== "VERIFICACAO_ENVIADA" && (
                      <button
                        type="button"
                        onClick={() => setUltimo(pedido)}
                        style={{ border: `1px solid ${cores.line}`, background: "#fff", color: cores.blue, borderRadius: 1000, padding: "6px 12px", font: "inherit", fontSize: 12, cursor: "pointer" }}
                      >
                        Ver link
                      </button>
                    )}
                  </span>
                </ItemDePedido>
              );
            })}
          </div>
        </div>
      )}
    </Painel>
  );
};
